import React from "react";

const ProtocolShellContext = React.createContext(null);
const STORAGE_KEY = "protocolShell.activeMatchId";

export function ProtocolShellProvider({ children }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedOfficialId, setSelectedOfficialId] = React.useState(null);
  const [activeProtocolMatchId, setActiveProtocolMatchId] = React.useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [resumeToken, setResumeToken] = React.useState(0);
  const [menuToken, setMenuToken] = React.useState(0);
  const [minimizeToken, setMinimizeToken] = React.useState(0);
  const [lineupAlertFocusToken, setLineupAlertFocusToken] = React.useState(0);
  const [lineupAlertFocusMatchId, setLineupAlertFocusMatchId] = React.useState(null);
  const [pendingSwitchMatchId, setPendingSwitchMatchId] = React.useState(null);
  const [pendingOfficialId, setPendingOfficialId] = React.useState(null);
  const [isWidgetDragging, setIsWidgetDragging] = React.useState(false);

  const openShell = React.useCallback(() => {
    setSelectedOfficialId(null);
    setIsOpen(true);
  }, []);

  const openShellMenu = React.useCallback(() => {
    setSelectedOfficialId(null);
    setMenuToken((prev) => prev + 1);
    setIsOpen(true);
  }, []);

  const openOfficialShell = React.useCallback((officialId) => {
    if (!officialId) return;
    setSelectedOfficialId(String(officialId));
    setIsOpen(true);
  }, []);

  const requestOfficialShell = React.useCallback(
    (officialId) => {
      if (!officialId) return;
      const nextId = String(officialId);
      if (activeProtocolMatchId) {
        setPendingOfficialId(nextId);
        return;
      }
      openOfficialShell(nextId);
    },
    [activeProtocolMatchId, openOfficialShell]
  );

  const closeShell = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const startProtocolSession = React.useCallback((matchId) => {
    if (!matchId) return;
    setActiveProtocolMatchId(String(matchId));
    setIsOpen(true);
  }, []);

  const requestProtocolSession = React.useCallback(
    (matchId) => {
      if (!matchId) return;
      const nextId = String(matchId);
      if (activeProtocolMatchId && activeProtocolMatchId !== nextId) {
        setPendingSwitchMatchId(nextId);
        return;
      }
      startProtocolSession(nextId);
    },
    [activeProtocolMatchId, startProtocolSession]
  );

  const minimizeProtocolSession = React.useCallback(() => {
    setIsOpen(false);
    setMinimizeToken((prev) => prev + 1);
  }, []);

  const resumeProtocolSession = React.useCallback(() => {
    if (!activeProtocolMatchId) return;
    setResumeToken((prev) => prev + 1);
    setIsOpen(true);
  }, [activeProtocolMatchId]);

  const focusProtocolLineupAlert = React.useCallback((matchId) => {
    if (!matchId) return;
    setLineupAlertFocusMatchId(String(matchId));
    setLineupAlertFocusToken((prev) => prev + 1);
    setIsOpen(true);
  }, []);

  const clearProtocolSession = React.useCallback(() => {
    setActiveProtocolMatchId(null);
    setPendingSwitchMatchId(null);
    setPendingOfficialId(null);
    setSelectedOfficialId(null);
    setIsOpen(false);
  }, []);

  const clearActiveProtocol = React.useCallback(() => {
    setActiveProtocolMatchId(null);
  }, []);

  const cancelProtocolSwitch = React.useCallback(() => {
    setPendingSwitchMatchId(null);
  }, []);

  const cancelOfficialSwitch = React.useCallback(() => {
    setPendingOfficialId(null);
  }, []);

  const confirmProtocolSwitch = React.useCallback(() => {
    if (!pendingSwitchMatchId) return;
    startProtocolSession(pendingSwitchMatchId);
    setPendingSwitchMatchId(null);
  }, [pendingSwitchMatchId, startProtocolSession]);

  const confirmOfficialSwitch = React.useCallback(() => {
    if (!pendingOfficialId) return;
    setActiveProtocolMatchId(null);
    setPendingSwitchMatchId(null);
    setSelectedOfficialId(String(pendingOfficialId));
    setPendingOfficialId(null);
    setIsOpen(true);
  }, [pendingOfficialId]);

  React.useEffect(() => {
    try {
      if (activeProtocolMatchId) {
        sessionStorage.setItem(STORAGE_KEY, activeProtocolMatchId);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore storage issues (private mode / blocked storage)
    }
  }, [activeProtocolMatchId]);

  const value = React.useMemo(
    () => ({
      isOpen,
      openShell,
      openShellMenu,
      openOfficialShell,
      requestOfficialShell,
      closeShell,
      setIsOpen,
      selectedOfficialId,
      activeProtocolMatchId,
      hasActiveProtocol: Boolean(activeProtocolMatchId),
      startProtocolSession,
      requestProtocolSession,
      minimizeProtocolSession,
      resumeProtocolSession,
      focusProtocolLineupAlert,
      clearProtocolSession,
      clearActiveProtocol,
      pendingSwitchMatchId,
      switchPromptOpen: Boolean(pendingSwitchMatchId),
      confirmProtocolSwitch,
      cancelProtocolSwitch,
      pendingOfficialId,
      officialSwitchPromptOpen: Boolean(pendingOfficialId),
      confirmOfficialSwitch,
      cancelOfficialSwitch,
      isWidgetDragging,
      setIsWidgetDragging,
      resumeToken,
      menuToken,
      minimizeToken,
      lineupAlertFocusToken,
      lineupAlertFocusMatchId,
    }),
    [
      isOpen,
      openShell,
      openShellMenu,
      openOfficialShell,
      requestOfficialShell,
      closeShell,
      selectedOfficialId,
      activeProtocolMatchId,
      startProtocolSession,
      requestProtocolSession,
      minimizeProtocolSession,
      resumeProtocolSession,
      focusProtocolLineupAlert,
      clearProtocolSession,
      clearActiveProtocol,
      pendingSwitchMatchId,
      confirmProtocolSwitch,
      cancelProtocolSwitch,
      pendingOfficialId,
      confirmOfficialSwitch,
      cancelOfficialSwitch,
      isWidgetDragging,
      resumeToken,
      menuToken,
      minimizeToken,
      lineupAlertFocusToken,
      lineupAlertFocusMatchId,
    ]
  );

  return (
    <ProtocolShellContext.Provider value={value}>
      {children}
    </ProtocolShellContext.Provider>
  );
}

export function useProtocolShell() {
  const ctx = React.useContext(ProtocolShellContext);
  if (!ctx) {
    throw new Error("useProtocolShell must be used inside ProtocolShellProvider");
  }
  return ctx;
}
